import type { FunctionComponent } from 'react';
import { useContext, useEffect, useState } from 'react';
import { ActionIcon, Button, Card, Center, Group, Image, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { useTranslation } from 'i18n';
import { logException } from 'helpers/loggers';
import MenuEdit from 'components/MenuManagement/MenuEdit/MenuEdit';

import { PhotoDrawer } from 'components/MenuManagement/MenuEdit/MenuPhotos/PhotoDrawer';
import type { CropState } from 'components/MenuManagement/MenuEdit/MenuPhotos/PhotoDrawer/EditPhotoContent';
import { cropImageToBlob } from 'components/MenuManagement/MenuEdit/MenuPhotos/PhotoDrawer/cropImageToBlob';
import { uploadCroppedPhoto } from 'components/MenuManagement/MenuEdit/MenuPhotos/PhotoDrawer/uploadCroppedPhoto';
import { EditIconSm } from '@chownow/pantry/icons';

import type { StagedImageFile } from './types';

const NS = 'menuManagementFields';
const THUMBNAIL_WIDTH = 128;
const THUMBNAIL_HEIGHT = 96;

type PhotoThumbnailFieldProps = {
  form: {
    values: { imageUrl?: string };
    setFieldValue: (field: string, value: string) => void;
  };
  record?: { imageUrl?: string; name?: string; uuid?: string; companyId?: string };
  onRemove?: () => void;
  setUploadImageFile?: (file: StagedImageFile | null) => void;
  isUploading?: boolean;
};

export const PhotoThumbnailField: FunctionComponent<PhotoThumbnailFieldProps> = ({
  form,
  record,
  onRemove,
  setUploadImageFile,
  isUploading,
}) => {
  const { t } = useTranslation(NS);
  const { menu, refetchPhotos, setImageOverride, imageOverrides } = useContext(MenuEdit.Context);

  const activeIntegrationNames = new Set<string>(
    (menu?.integrations ?? [])
      .filter((i: { isDeleted?: boolean }) => !i.isDeleted)
      .map((i: { integrationName: string }) => i.integrationName),
  );
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [photoDrawerOpened, { open: openPhotoDrawer, close: closePhotoDrawer }] = useDisclosure();

  useEffect(
    () => () => {
      if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  const savedImageUrl = form.values.imageUrl ? record?.imageUrl || form.values.imageUrl : null;
  const overrideUrl = record?.uuid ? imageOverrides?.[record.uuid] : undefined;
  const displayUrl = previewUrl || overrideUrl || savedImageUrl;
  const hasPhoto = !!displayUrl;

  // No-op: PhotoDrawer captures the blob URL internally. The actual upload to Cuboh
  // happens on save with the cropped result. We let the parent form know a file is pending.
  const handleUpload = (file: File): Promise<void> => {
    setUploadImageFile?.({ name: record?.uuid, originFileObj: file });
    return Promise.resolve();
  };

  const handleSavePhoto = async (
    cropState: CropState,
    imageUrl: string,
    enhancedAt?: Date | null,
  ): Promise<void> => {
    if (!record?.companyId || !record?.uuid || menu?.merchantId == null) {
      // No Cuboh identity yet (new item) or no merchant context: crop locally, show a
      // preview, and stage the cropped file so formSubmitHandler uploads on submit.
      // An existing item missing merchantId is unexpected — log it but keep the fallback.
      if (record?.uuid && menu?.merchantId == null) {
        logException({
          message: 'PhotoThumbnailField: existing item saved a crop without menu merchantId',
          error: null,
          context: { itemUuid: record.uuid },
          tags: null,
        });
      }
      const croppedFile = await cropImageToBlob(imageUrl, cropState);
      const preview = URL.createObjectURL(croppedFile);
      if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(preview);
      setUploadImageFile?.({
        name: croppedFile.name,
        originFileObj: croppedFile,
        imageEnhancedAt: enhancedAt?.toISOString(),
      });
      return;
    }
    const { url } = await uploadCroppedPhoto(imageUrl, cropState, {
      companyId: record.companyId,
      enhancedAt: enhancedAt?.toISOString(),
      merchantId: menu.merchantId,
      itemUuid: record.uuid,
    });
    if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(url);
    form.setFieldValue('imageUrl', url);
    setUploadImageFile?.(null);
    setImageOverride?.(record.uuid, url);
    refetchPhotos();
  };

  const handleRemove = () => {
    if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    form.setFieldValue('imageUrl', '');
    if (record?.uuid) setImageOverride?.(record.uuid, undefined);
    onRemove?.();
  };

  return (
    <>
      <Card variant="outlined" p="xs2" radius="xs">
        <Group gap="sm" align="center" wrap="nowrap">
          <Card
            w={THUMBNAIL_WIDTH}
            h={THUMBNAIL_HEIGHT}
            p={0}
            bg="var(--surface-tertiary)"
            radius="xs"
            style={{ flexShrink: 0 }}
          >
            {hasPhoto ? (
              <Image
                src={displayUrl}
                w={THUMBNAIL_WIDTH}
                h={THUMBNAIL_HEIGHT}
                fit="cover"
                style={{ opacity: isUploading ? 0.5 : 1 }}
              />
            ) : (
              <Center h="100%">
                <ActionIcon
                  variant="filled"
                  size={32}
                  aria-label={t('PhotoThumbnailField.addPhoto')}
                  onClick={openPhotoDrawer}
                  radius="xl"
                >
                  <EditIconSm />
                </ActionIcon>
              </Center>
            )}
          </Card>

          {hasPhoto ? (
            <Stack gap="sm">
              {savedImageUrl && (
                <Text variant="sublabel-sm" lineClamp={3} style={{ wordBreak: 'break-all' }}>
                  {savedImageUrl}
                </Text>
              )}
              <Group gap="xs">
                <Button variant="link" onClick={openPhotoDrawer} disabled={isUploading}>
                  {t('PhotoThumbnailField.replace')}
                </Button>
                <Button variant="link" onClick={handleRemove} disabled={isUploading}>
                  {t('PhotoThumbnailField.delete')}
                </Button>
              </Group>
            </Stack>
          ) : (
            <Text>{t('PhotoThumbnailField.uploadPrompt')}</Text>
          )}
        </Group>
      </Card>
      <PhotoDrawer
        opened={photoDrawerOpened}
        onClose={closePhotoDrawer}
        itemName={record?.name ?? ''}
        companyId={record?.companyId != null ? String(record.companyId) : undefined}
        menuId={menu?.uuid}
        itemId={record?.uuid}
        activeIntegrationNames={activeIntegrationNames}
        onUpload={handleUpload}
        onSave={handleSavePhoto}
      />
    </>
  );
};
